import { useNavigate, useInRouterContext, BrowserRouter } from 'react-router-dom';

import { useBillingInfo } from '../hooks/useBillingInfo';
import { useI18n } from '../i18n/provider';

import { Header } from './Header';
import { Button } from './ui/Button';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps): JSX.Element {
  const { t } = useI18n();
  // Guard against accidental render outside Router context.
  const inRouter = useInRouterContext();
  const { data: billingData } = useBillingInfo();
  const navigate = useNavigate();
  const usage = billingData
    ? {
        used: billingData.data.currentMonth?.total || 0,
        limit: billingData.data.plan?.limits?.monthlyVerifications || 1000,
      }
    : null;

  const showBanner = usage != null && usage.limit > 0 && usage.used / usage.limit >= 0.8;

  const content = (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <Header />
      {usage != null && usage.limit > 0 && usage.used / usage.limit >= 0.8 && (
        <div className="fixed top-[72px] md:top-[88px] left-0 right-0 z-40 bg-amber-50/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="text-xs sm:text-sm text-amber-900">
              {t('layout.quotaBanner', {
                percent: Math.round((usage.used / usage.limit) * 100),
                used: usage.used,
                limit: usage.limit,
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto whitespace-nowrap"
              onClick={() => {
                navigate('/admin/billing');
              }}
            >
              {t('common.upgrade')}
            </Button>
          </div>
        </div>
      )}
      <main
        className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${
          showBanner ? 'pt-[140px] md:pt-[156px]' : 'pt-[92px] md:pt-[108px]'
        } pb-10 min-h-[100dvh] overflow-y-auto ios-momentum touch-pan-y`}
      >
        {children}
      </main>
    </div>
  );
  return inRouter ? content : <BrowserRouter>{content}</BrowserRouter>;
}
